import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ResizeAware from 'react-resize-aware';
import cloneDeep from 'lodash/cloneDeep';
import indexOf from 'lodash/indexOf';
import pullAt from 'lodash/pullAt';
import AutocodeGrid from './AutocodeGrid/AutocodeGrid';
import { checkWeightType } from '../../util/CommonUtils';
import ACGridContext from './ACContext/ACGridContext';
import * as MenuTypesOptions from '../common/MenuTypesOptions';
import AC_GRID from './AutocodeTypes';
import { updateCrosstabTitleHeader } from '../../util/autocodeUtils';
import {
  getDatapointsForSingleList,
  getDatapointsForTemplate4,
  getDatapointsForTemplate1Category,
  getDatapointsForTemplate1BrandTypeKindForm,
  getDatapointsForTemplate278,
} from '../../util/AutocodeDragAndDropUtils';
import {
  BASES_DATA_GRID,
  COLUMNS_DATA_GRID,
  ROWS_DATA_GRID,
  AC_NODETYPE_DEFINITION,
  AC_NODETYPE_DATAPOINT,
  AC_NODETYPE_OPERATOR,
  AC_OPERATORS,
  AC_NODETYPE_FUNC,
} from './AutocodeConstants';
import ACCRBGridDeleteModal from './AutocodeGrid/ACCRBGridDeleteModal';
import AutocodeSampleWeighted from './AutocodeSelectionBox/AutocodeSampleWeighted';
import AutocodeSelectionAction from './AutocodeSelectionBox/AutocodeSelectionActions';
import { useACSelectionBoxContext } from './ACContext/ACSelectionBoxContext';
import { checkAndAddFunctionInAutocodeNode } from './AutocodeFunctions';
import { wrapNode, wrapNodes } from './ACContext/acSelectionBoxReducer';

const AutocodeCRBGrid = props => {
  const {
    weightTypes,
    gridTitle,
    BasesDataGridData,
    ColumnsDataGridData,
    RowsDataGridData,
    moduleAction,
    moduleStudy,
    gridData,
    selectedWeightType,
    activeAccountId,
    uid,
    sampleWeightedKPIs,
    partsToShow,
  } = props;
  const [CRBGridsWidth, setCRBGridsWidth] = useState();
  const [allBasesLists, setAllBasesLists] = useState([]);
  const [allColumnsLists, setAllColumnsLists] = useState([]);
  const [allRowsLists, setAllRowsLists] = useState([]);
  const [selectedBasesData, setSelectedBasesData] = useState([]);
  const [selectedColumnData, setSelectedColumnData] = useState([]);
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllGridType, setDeleteAllGridType] = useState('');
  const [checkBlur, setCheckBlur] = useState(true);
  const { SampleWeightedCall } = AutocodeSampleWeighted();
  const acSelContext = useACSelectionBoxContext();
  const { insertNodes } = AutocodeSelectionAction(
    acSelContext.state,
    acSelContext.dispatch
  );

  const getGridDataByCategory = gridCategory => {
    let gridData = [];

    if (gridCategory === BASES_DATA_GRID) {
      gridData = [...allBasesLists];
    } else if (gridCategory === COLUMNS_DATA_GRID) {
      gridData = [...allColumnsLists];
    } else if (gridCategory === ROWS_DATA_GRID) {
      gridData = [...allRowsLists];
    }

    return gridData;
  };

  useEffect(() => {
    setTimeout(() => {
      if (BasesDataGridData && BasesDataGridData.length > 0) {
        const userDefinedBases = cloneDeep(BasesDataGridData);
        userDefinedBases.forEach(item => {
          item.type = 'user';
        });
        setAllBasesLists([...userDefinedBases]);
      }
    }, 0);

    return () => {
      setAllBasesLists([]);
    };
  }, [JSON.stringify(BasesDataGridData)]);

  useEffect(() => {
    setTimeout(() => {
      setAllColumnsLists(ColumnsDataGridData);
    }, 0);

    return () => {
      setAllColumnsLists([]);
    };
  }, [JSON.stringify(ColumnsDataGridData)]);

  useEffect(() => {
    setTimeout(() => {
      setAllRowsLists(RowsDataGridData);
    }, 0);

    return () => {
      setAllRowsLists([]);
    };
  }, [JSON.stringify(RowsDataGridData)]);

  // handler for different drag/drop scenarios to report preview
  const handleGridDrop = (dropItems, dropItemType, componentGridCategory) => {
    if (dropItemType === AC_GRID.AC_GRID_1) {
      // report preview -> report peview
      if (!dropItems.ACTemplateType) {
        const itemsInSource = dropItems.gridarray.filter(i => i.id);
        const itemIdsToRemove = dropItems.selectedNodes.map(i => i.id);
        const itemsToRemoveFromSource = itemsInSource.filter(
          i => !itemIdsToRemove.includes(i.id)
        );

        // remove items from source grid
        moduleAction.updateSampleWeighted(
          itemsToRemoveFromSource,
          dropItems.gridCategory
        );

        // add items to target grid
        const newGridData = getGridDataByCategory(componentGridCategory);
        moduleAction.updateSampleWeighted(
          [...newGridData, ...dropItems.selectedNodes],
          componentGridCategory
        );
        return;
      }

      // middle section -> report preview
      let nodes = [];
      // get selected datapoints from middle section
      // wrap them in nodes before calling sampleweighted
      // to send them to report preview
      switch (dropItems.ACTemplateType) {
        case AC_GRID.AC_TEMPLATE_SINGLE_LIST: {
          const datapoints = getDatapointsForSingleList(
            dropItems.selectedNodes,
            dropItems.selectedNodeAnswers
          );
          nodes = wrapNodes(AC_NODETYPE_DATAPOINT, datapoints);
          break;
        }
        case AC_GRID.AC_TEMPLATE_4: {
          const datapoints = getDatapointsForTemplate4(
            dropItems.selectedNodes,
            dropItems.selectedNodeAnswers,
            dropItems.userOptions,
            dropItems.selectedUserOption
          );
          nodes = wrapNodes(AC_NODETYPE_DATAPOINT, datapoints);
          break;
        }
        case AC_GRID.AC_TEMPLATE_1_CATEGORY: {
          const datapoints = getDatapointsForTemplate1Category(
            dropItems.selectedNodes,
            dropItems.selectedNodeAnswers,
            dropItems.userOptions,
            dropItems.selectedUserOption,
            dropItems.totalUsersItem
          );
          nodes = wrapNodes(AC_NODETYPE_DATAPOINT, datapoints);
          break;
        }
        case AC_GRID.AC_TEMPLATE_1_BRAND_TYPE_KIND_FORM: {
          const datapoints = getDatapointsForTemplate1BrandTypeKindForm(
            dropItems.selectedNodes,
            dropItems.selectedNodeAnswers,
            dropItems.userOptions,
            dropItems.selectedUserOption,
            dropItems.onePlusSelected,
            dropItems.onePlusValue
          );
          nodes = wrapNodes(AC_NODETYPE_DATAPOINT, datapoints);
          break;
        }
        case AC_GRID.AC_TEMPLATE_2:
        case AC_GRID.AC_TEMPLATE_7: {
          // Datapoints here will be array of arrays
          const datapoints = getDatapointsForTemplate278(
            dropItems.selectedList1,
            dropItems.selectedList2,
            dropItems.selectedNodeAnswers
          );
          // If OR is not checked, flatten the arrays and wrap them as nodes.
          if (!dropItems.or) {
            nodes = wrapNodes(AC_NODETYPE_DATAPOINT, datapoints.flat());
            break;
          }
          /*
            If OR is checked, create a binary tree structure with the datapoints of each array.
            Keep creating OR nodes for every two items setting the previous OR as one of the children
            For example, three nodes A, B, C would look like...
            - OR
              - A
              - OR
                - B
                - C
            This logic mirrors insertOperatorNode function in acSelectionBoxReducer.
          */
          nodes = datapoints.map(d => {
            // Wrap datapoints as nodes
            const hNodes = wrapNodes(AC_NODETYPE_DATAPOINT, d);
            // Keep track of the last OR created as topNode
            let topNode = null;
            while (hNodes.length > 0) {
              // if topNode hasn't been created
              if (topNode == null) {
                // take first two datapoints
                const pulled = pullAt(hNodes, [0, 1]);
                // create an OR node
                const orNode = wrapNode(
                  AC_NODETYPE_OPERATOR,
                  AC_OPERATORS.type.or
                );
                orNode.data = { selected: false, type: AC_OPERATORS.type.or };
                // set the first two datapoints as it children
                orNode.children = [pulled[0], pulled[1]];
                // make the OR with children the topNode
                topNode = orNode;
              } else {
                // take next one datapoint
                const pulled = pullAt(hNodes, [0]);
                // create an OR node
                const orNode = wrapNode(
                  AC_NODETYPE_OPERATOR,
                  AC_OPERATORS.type.or
                );
                orNode.data = { selected: false, type: AC_OPERATORS.type.or };
                // set the datapoint and the previous topNode as its children
                orNode.children = [topNode, pulled[0]];
                // make the OR node with children the topNode
                topNode = orNode;
              }
            }
            return topNode;
          });
          break;
        }
        case AC_GRID.AC_TEMPLATE_8: {
          // Datapoints here will be array of arrays
          const datapoints = getDatapointsForTemplate278(
            dropItems.selectedList1,
            dropItems.selectedList2,
            dropItems.selectedNodeAnswers
          );
          /*
            How the nodes are created depends on if OR is checked and if they are netany.
            This should mirror what insertNetanyLeafNode and insertOperatorNetAnyNode do in selection box.
          */
          nodes = datapoints
            .map(dps => {
              // check if dps are netany datapoints
              const isNetAny = dps.some(
                ({ keyword }) => keyword.toUpperCase().slice(0, 3) === 'XNA'
              );
              // OR is checked and dps are netany
              if (dropItems.or && isNetAny) {
                const netAnyNode = wrapNode(AC_NODETYPE_FUNC, {
                  funcName: 'net any',
                  selected: false,
                  editable: true,
                  params: { operator: AC_OPERATORS.type.or },
                });
                const children = wrapNodes(AC_NODETYPE_DATAPOINT, dps);
                return {
                  ...netAnyNode,
                  children,
                };
              }
              // OR is checked and dps are not netany
              if (dropItems.or && !isNetAny) {
                // This condition is same as template 2/7. See above for details.
                // Wrap datapoints as nodes
                const hNodes = wrapNodes(AC_NODETYPE_DATAPOINT, dps);
                // Keep track of the last OR created as topNode
                let topNode = null;
                while (hNodes.length > 0) {
                  // if topNode hasn't been created
                  if (topNode == null) {
                    // take first two datapoints
                    const pulled = pullAt(hNodes, [0, 1]);
                    // create an OR node
                    const orNode = wrapNode(
                      AC_NODETYPE_OPERATOR,
                      AC_OPERATORS.type.or
                    );
                    orNode.data = {
                      selected: false,
                      type: AC_OPERATORS.type.or,
                    };
                    // set the first two datapoints as it children
                    orNode.children = [pulled[0], pulled[1]];
                    // make the OR with children the topNode
                    topNode = orNode;
                  } else {
                    // take next one datapoint
                    const pulled = pullAt(hNodes, [0]);
                    // create an OR node
                    const orNode = wrapNode(
                      AC_NODETYPE_OPERATOR,
                      AC_OPERATORS.type.or
                    );
                    orNode.data = {
                      selected: false,
                      type: AC_OPERATORS.type.or,
                    };
                    // set the datapoint and the previous topNode as its children
                    orNode.children = [topNode, pulled[0]];
                    // make the OR node with children the topNode
                    topNode = orNode;
                  }
                }
                return topNode;
              }
              // OR is not checked and dps are netany
              if (!dropItems.or && isNetAny) {
                return dps.map(dp => {
                  const netAnyNode = wrapNode(AC_NODETYPE_FUNC, {
                    funcName: 'net any',
                    selected: false,
                    editable: false,
                  });
                  const children = wrapNodes(AC_NODETYPE_DATAPOINT, [dp]);
                  return {
                    ...netAnyNode,
                    children,
                  };
                });
              }
              // OR is not checked and dps are not netany
              return wrapNodes(AC_NODETYPE_DATAPOINT, dps.flat());
            })
            .flat(); // flatten array at the end
          break;
        }
        default: {
          // if template type is not recognized
          // this will result in a no-op
          nodes = [];
        }
      }

      // get sampleweighted values for nodes
      // and send to correct grid
      SampleWeightedCall(
        componentGridCategory,
        nodes,
        gridData,
        moduleStudy,
        selectedWeightType,
        activeAccountId,
        uid,
        sampleWeightedKPIs
      );
    } else if (
      dropItems.selectedNodes.length > 0 &&
      dropItemType === AC_GRID.AC_GRID_2
    ) {
      // selection box -> report preview
      SampleWeightedCall(
        componentGridCategory,
        dropItems.selectedNodes,
        gridData,
        moduleStudy,
        selectedWeightType,
        activeAccountId,
        uid,
        sampleWeightedKPIs
      );
    } else if (
      dropItems.selectedNodes.length > 0 &&
      dropItemType === AC_GRID.AC_DEFINITIONS
    ) {
      // definitions middle section -> report preview
      const selectedNodes = wrapNodes(
        AC_NODETYPE_DEFINITION,
        dropItems.selectedNodes
      );
      SampleWeightedCall(
        componentGridCategory,
        selectedNodes,
        gridData,
        moduleStudy,
        selectedWeightType,
        activeAccountId,
        uid,
        sampleWeightedKPIs
      );
    }
  };

  const handleCRBGridsResize = dimensions => {
    setCRBGridsWidth(dimensions.width);
  };

  const getGridColumns = titleColumnName => {
    const headers = [
      {
        key: 'title',
        name: titleColumnName,
      },
    ];
    if (moduleStudy && !moduleStudy.st.toLowerCase().includes('slcl')) {
      headers.push({
        key: 'sample',
        name: 'Smpl',
      });
      headers.push({
        key: 'weighted',
        name: `Wghtd`,
      });
    }
    return headers;
  };

  const handleRowUpdated = (rowData, gridCategory) => {
    const gridarray = getGridDataByCategory(gridCategory);
    const rowIndex = gridarray.findIndex(
      row => row.keyword === rowData.keyword
    );
    if (
      rowIndex < gridarray.length &&
      rowData.updated &&
      rowData.updated.title
    ) {
      const rows = cloneDeep([...gridarray]);
      let originalT = rows[rowIndex].originalTitle.trim();
      const updatedT = rowData.updated.title.trim();
      if (originalT === updatedT) {
        originalT =
          rows[rowIndex].dataSmartSearch &&
          rows[rowIndex].dataSmartSearch.objectsAdded &&
          rows[rowIndex].dataSmartSearch.objectsAdded[0]
            ? rows[rowIndex].dataSmartSearch.objectsAdded[0].shortText
            : rows[rowIndex].originalTitle.trim();
      }
      Object.assign(rows[rowIndex], rowData.updated);
      if (rowData.updated.title) {
        const { updateCrosstabGridTitleHeader } = moduleAction;
        updateCrosstabTitleHeader(
          {
            originalTitle: originalT,
            updatedTitle: updatedT,
            gridCategory,
            keyword: rows[rowIndex].keyword,
          },
          {
            originalList: rows,
            updateCrosstabGridTitleHeader,
          }
        );
        Object.assign(rows[rowIndex], { originalTitle: rowData.updated.title });
      }
      rows[rowIndex].isLabelUpdated = true;
      if (
        rows[rowIndex].dataSmartSearch &&
        rows[rowIndex].dataSmartSearch.autocodenode
      ) {
        rows[rowIndex].dataSmartSearch.autocodenode.useLabelOverride = true;
        rows[
          rowIndex
        ].dataSmartSearch.autocodenode.labelOverride = rowData.updated.title.trim();
      }
      moduleAction.updateSampleWeighted([...rows], gridCategory);
    }
  };

  const handleEditExpression = (data, gridCategory, gridData) => {
    // console.log('Data:', data.dataSmartSearch.autocodenode);
    // console.log('Grid Category:', gridCategory);
    // console.log('Grid Data: ', gridData);
    // console.log(
    //   checkAndAddFunctionInAutocodeNode(
    //     cloneDeep(data.dataSmartSearch.autocodenode)
    //   )
    // );

    insertNodes(
      [
        checkAndAddFunctionInAutocodeNode(
          cloneDeep(data.dataSmartSearch.autocodenode)
        ),
      ],
      '0',
      -1
    );

    moduleAction.updateSampleWeighted(
      // eslint-disable-next-line react/prop-types
      gridData.filter(({ id }) => id !== data.id),
      gridCategory
    );
  };

  // Context Menu Handlers
  const handleContextMenuOptions = ({ option, data, gridCategory }) => {
    const {
      DELETE_LABEL,
      DELETE_ALL,
      EDIT_ROW,
      EDIT_COLUMNS,
      EDIT_BASES,
      EDIT_EXPRESSION,
    } = MenuTypesOptions.CONTEXT_MENU_OPTIONS;
    const selectedList = getGridDataByCategory(gridCategory);
    switch (option) {
      case DELETE_ALL:
        // For Delete All Bases, Columns, Rows
        moduleAction.updateSampleWeighted([], gridCategory);
        break;
      case DELETE_LABEL:
        // For Delete Bases, Columns, Rows
        moduleAction.updateSampleWeighted(
          selectedList.filter(({ id }) => id !== data.id),
          gridCategory
        );
        break;
      case EDIT_COLUMNS:
        // For Edit Colums Title
        handleRowUpdated(data, COLUMNS_DATA_GRID);
        break;
      case EDIT_ROW:
        // For Edit Rows Title
        handleRowUpdated(data, ROWS_DATA_GRID);
        break;
      case EDIT_BASES:
        // For Edit Bases Title
        handleRowUpdated(data, BASES_DATA_GRID);
        break;
      case EDIT_EXPRESSION:
        // For Editing Expression in any Grid
        handleEditExpression(data, gridCategory, selectedList);
        break;
      default:
        return null;
    }
    return null;
  };

  const deleteGrid = gridType => {
    setDeleteAllGridType(gridType);
    setShowDeleteAllModal(true);
  };

  const keyHandler = useCallback(
    event => {
      if (event.key === 'Delete') {
        const gridCategory =
          selectedBasesData && selectedBasesData.length > 0
            ? BASES_DATA_GRID
            : selectedColumnData && selectedColumnData.length > 0
            ? COLUMNS_DATA_GRID
            : ROWS_DATA_GRID;
        const selectedGridData = getGridDataByCategory(gridCategory);
        const selectedFilteringData =
          gridCategory === BASES_DATA_GRID
            ? selectedBasesData
            : gridCategory === COLUMNS_DATA_GRID
            ? selectedColumnData
            : selectedRowsData;
        const data = cloneDeep(selectedGridData).filter(
          ({ id }) => selectedFilteringData.includes(id) !== true
        );
        moduleAction.updateSampleWeighted(data, gridCategory);
      }
    },
    [
      allBasesLists,
      allColumnsLists,
      allRowsLists,
      selectedBasesData,
      selectedColumnData,
      selectedRowsData,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [keyHandler]);

  /* after testing commented section will be deleted... */
  const handleOnSelectRow = (gridRow, gridType, event) => {
    let newSelectedData = [];
    if (event.ctrlKey) {
      if (gridType === BASES_DATA_GRID) {
        setSelectedColumnData([]);
        setSelectedRowsData([]);
        newSelectedData = [...cloneDeep(selectedBasesData)];
        if (newSelectedData.includes(gridRow)) {
          newSelectedData = cloneDeep(newSelectedData).filter(
            id => gridRow !== id
          );
        } else {
          newSelectedData.push(gridRow);
        }
        setSelectedBasesData([...newSelectedData]);
      } else if (gridType === COLUMNS_DATA_GRID) {
        setSelectedBasesData([]);
        setSelectedRowsData([]);
        newSelectedData = [...cloneDeep(selectedColumnData)];
        if (newSelectedData.includes(gridRow)) {
          newSelectedData = cloneDeep(newSelectedData).filter(
            id => gridRow !== id
          );
        } else {
          newSelectedData.push(gridRow);
        }
        setSelectedColumnData([...newSelectedData]);
      } else if (gridType === ROWS_DATA_GRID) {
        setSelectedBasesData([]);
        setSelectedColumnData([]);
        newSelectedData = [...cloneDeep(selectedRowsData)];
        if (newSelectedData.includes(gridRow)) {
          newSelectedData = cloneDeep(newSelectedData).filter(
            id => gridRow !== id
          );
        } else {
          newSelectedData.push(gridRow);
        }
        setSelectedRowsData([...newSelectedData]);
      }
    } else if (event.shiftKey) {
      let localSelectedGridData = [];
      let localAllGridData = [];
      if (gridType === BASES_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedBasesData)];
        localAllGridData = [...cloneDeep(allBasesLists).map(({ id }) => id)];
      } else if (gridType === COLUMNS_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedColumnData)];
        localAllGridData = [...cloneDeep(allColumnsLists).map(({ id }) => id)];
      } else if (gridType === ROWS_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedRowsData)];
        localAllGridData = [...cloneDeep(allRowsLists).map(({ id }) => id)];
      }
      if (localSelectedGridData.length > 0) {
        const lastRow = localSelectedGridData[0];
        const startIndex = localAllGridData.indexOf(lastRow);
        const endIndex = localAllGridData.indexOf(gridRow);
        let fromIndex = 0;
        let toIndex = 0;
        if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
          fromIndex = startIndex;
          toIndex = endIndex;
        } else {
          fromIndex = endIndex;
          toIndex = startIndex;
        }
        newSelectedData = [
          ...cloneDeep(localAllGridData.slice(fromIndex, toIndex + 1)),
        ];
      } else {
        newSelectedData.push(gridRow);
      }
      if (gridType === BASES_DATA_GRID) {
        setSelectedBasesData([...newSelectedData]);
        setSelectedColumnData([]);
        setSelectedRowsData([]);
      } else if (gridType === COLUMNS_DATA_GRID) {
        setSelectedColumnData([...newSelectedData]);
        setSelectedBasesData([]);
        setSelectedRowsData([]);
      } else if (gridType === ROWS_DATA_GRID) {
        setSelectedRowsData([...newSelectedData]);
        setSelectedBasesData([]);
        setSelectedColumnData([]);
      }
    } else {
      let localSelectedGridData = [];
      if (gridType === BASES_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedBasesData)];
      } else if (gridType === COLUMNS_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedColumnData)];
      } else if (gridType === ROWS_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedRowsData)];
      }
      setSelectedBasesData([]);
      setSelectedColumnData([]);
      setSelectedRowsData([]);
      if (!localSelectedGridData.includes(gridRow)) {
        newSelectedData.push(gridRow);
      } else {
        newSelectedData = [];
      }
      if (gridType === BASES_DATA_GRID) {
        setSelectedBasesData([...newSelectedData]);
      } else if (gridType === COLUMNS_DATA_GRID) {
        setSelectedColumnData([...newSelectedData]);
      } else if (gridType === ROWS_DATA_GRID) {
        setSelectedRowsData([...newSelectedData]);
      }
    }
  };

  const clearSelections = () => {
    if (checkBlur) {
      setSelectedBasesData([]);
      setSelectedColumnData([]);
      setSelectedRowsData([]);
    }
  };

  const handleRowMouseDown = (gridRow, gridType, e) => {
    if (gridRow) {
      let newSelectedData = [];
      let selectedData = [];
      if (gridType === BASES_DATA_GRID) {
        selectedData = selectedBasesData;
      } else if (gridType === COLUMNS_DATA_GRID) {
        selectedData = selectedColumnData;
      } else if (gridType === ROWS_DATA_GRID) {
        selectedData = selectedRowsData;
      }
      const seletedIndex = indexOf(selectedData, gridRow);
      if (
        e.button === 0 &&
        !e.ctrlKey &&
        !e.shiftKey &&
        seletedIndex === -1
        // !this.state.rows[row.idx].isSelected
      ) {
        let localSelectedGridData = [];
        if (gridType === BASES_DATA_GRID) {
          localSelectedGridData = [...cloneDeep(selectedBasesData)];
        } else if (gridType === COLUMNS_DATA_GRID) {
          localSelectedGridData = [...cloneDeep(selectedColumnData)];
        } else if (gridType === ROWS_DATA_GRID) {
          localSelectedGridData = [...cloneDeep(selectedRowsData)];
        }
        setSelectedBasesData([]);
        setSelectedColumnData([]);
        setSelectedRowsData([]);
        if (!localSelectedGridData.includes(gridRow)) {
          newSelectedData.push(gridRow);
        } else {
          localSelectedGridData.push(gridRow);
          newSelectedData = localSelectedGridData;
          newSelectedData = [];
        }
        if (gridType === BASES_DATA_GRID) {
          setSelectedBasesData([...newSelectedData]);
        } else if (gridType === COLUMNS_DATA_GRID) {
          setSelectedColumnData([...newSelectedData]);
        } else if (gridType === ROWS_DATA_GRID) {
          setSelectedRowsData([...newSelectedData]);
        }
      }
    } else {
      setSelectedBasesData([]);
      setSelectedColumnData([]);
      setSelectedRowsData([]);
    }
  };

  const handleRowMouseUp = (gridRow, gridType, e) => {
    let selectedData = [];
    if (gridType === BASES_DATA_GRID) {
      selectedData = selectedBasesData;
    } else if (gridType === COLUMNS_DATA_GRID) {
      selectedData = selectedColumnData;
    } else if (gridType === ROWS_DATA_GRID) {
      selectedData = selectedRowsData;
    }
    const seletedIndex = indexOf(selectedData, gridRow);
    let newSelectedData = [];
    if (
      e.button === 0 &&
      !e.ctrlKey &&
      !e.shiftKey &&
      seletedIndex !== -1 &&
      selectedData.length > 1
      // !this.state.rows[row.idx].isSelected
    ) {
      newSelectedData.push(gridRow);
      if (gridType === BASES_DATA_GRID) {
        setSelectedBasesData([...newSelectedData]);
      } else if (gridType === COLUMNS_DATA_GRID) {
        setSelectedColumnData([...newSelectedData]);
      } else if (gridType === ROWS_DATA_GRID) {
        setSelectedRowsData([...newSelectedData]);
      }
    }
    if (e.ctrlKey) {
      if (gridType === BASES_DATA_GRID) {
        setSelectedColumnData([]);
        setSelectedRowsData([]);
        newSelectedData = [...cloneDeep(selectedBasesData)];
        if (newSelectedData.includes(gridRow)) {
          newSelectedData = cloneDeep(newSelectedData).filter(
            id => gridRow !== id
          );
        } else {
          newSelectedData.push(gridRow);
        }
        setSelectedBasesData([...newSelectedData]);
      } else if (gridType === COLUMNS_DATA_GRID) {
        setSelectedBasesData([]);
        setSelectedRowsData([]);
        newSelectedData = [...cloneDeep(selectedColumnData)];
        if (newSelectedData.includes(gridRow)) {
          newSelectedData = cloneDeep(newSelectedData).filter(
            id => gridRow !== id
          );
        } else {
          newSelectedData.push(gridRow);
        }
        setSelectedColumnData([...newSelectedData]);
      } else if (gridType === ROWS_DATA_GRID) {
        setSelectedBasesData([]);
        setSelectedColumnData([]);
        newSelectedData = [...cloneDeep(selectedRowsData)];
        if (newSelectedData.includes(gridRow)) {
          newSelectedData = cloneDeep(newSelectedData).filter(
            id => gridRow !== id
          );
        } else {
          newSelectedData.push(gridRow);
        }
        setSelectedRowsData([...newSelectedData]);
      }
    }
    if (e.shiftKey) {
      let localSelectedGridData = [];
      let localAllGridData = [];
      if (gridType === BASES_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedBasesData)];
        localAllGridData = [...cloneDeep(allBasesLists).map(({ id }) => id)];
      } else if (gridType === COLUMNS_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedColumnData)];
        localAllGridData = [...cloneDeep(allColumnsLists).map(({ id }) => id)];
      } else if (gridType === ROWS_DATA_GRID) {
        localSelectedGridData = [...cloneDeep(selectedRowsData)];
        localAllGridData = [...cloneDeep(allRowsLists).map(({ id }) => id)];
      }
      if (localSelectedGridData.length > 0) {
        const lastRow = localSelectedGridData[0];
        const startIndex = localAllGridData.indexOf(lastRow);
        const endIndex = localAllGridData.indexOf(gridRow);
        let fromIndex = 0;
        let toIndex = 0;
        if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
          fromIndex = startIndex;
          toIndex = endIndex;
        } else {
          fromIndex = endIndex;
          toIndex = startIndex;
        }
        newSelectedData = [
          ...cloneDeep(localAllGridData.slice(fromIndex, toIndex + 1)),
        ];
      } else {
        newSelectedData.push(gridRow);
      }
      if (gridType === BASES_DATA_GRID) {
        setSelectedBasesData([...newSelectedData]);
        setSelectedColumnData([]);
        setSelectedRowsData([]);
      } else if (gridType === COLUMNS_DATA_GRID) {
        setSelectedColumnData([...newSelectedData]);
        setSelectedBasesData([]);
        setSelectedRowsData([]);
      } else if (gridType === ROWS_DATA_GRID) {
        setSelectedRowsData([...newSelectedData]);
        setSelectedBasesData([]);
        setSelectedColumnData([]);
      }
    }
  };

  return (
    <Fragment>
      {gridTitle ? <div className="grid-title">{gridTitle}</div> : null}
      <ACGridContext.Provider
        value={{ handleContextMenuOptions, handleOnSelectRow, deleteGrid }}
      >
        <ResizeAware
          style={{ position: 'relative', width: '100%' }}
          onlyEvent
          onResize={handleCRBGridsResize}
        >
          <AutocodeGrid
            gridCategory={BASES_DATA_GRID}
            title={getGridColumns('Bases')}
            weightType={`${checkWeightType(weightTypes)}`}
            minRows={3}
            gridarray={cloneDeep(allBasesLists) || []}
            onGridDrop={handleGridDrop}
            height="83px"
            width={CRBGridsWidth}
            selectedData={selectedBasesData}
            clearSelections={clearSelections}
            setCheckBlur={setCheckBlur}
            partsToShow={partsToShow}
            handleRowMouseDown={handleRowMouseDown}
            handleRowMouseUp={handleRowMouseUp}
          />
        </ResizeAware>
        <ResizeAware
          style={{ position: 'relative', width: '100%' }}
          onlyEvent
          onResize={handleCRBGridsResize}
        >
          <AutocodeGrid
            title={getGridColumns('Columns')}
            weightType={`${checkWeightType(weightTypes)}`}
            gridCategory={COLUMNS_DATA_GRID}
            gridarray={cloneDeep(allColumnsLists)}
            minRows={6}
            onGridDrop={handleGridDrop}
            width={CRBGridsWidth}
            height="164px"
            selectedData={selectedColumnData}
            clearSelections={clearSelections}
            setCheckBlur={setCheckBlur}
            partsToShow={partsToShow}
            handleRowMouseDown={handleRowMouseDown}
            handleRowMouseUp={handleRowMouseUp}
          />
        </ResizeAware>
        <ResizeAware
          style={{ position: 'relative', width: '100%' }}
          onlyEvent
          onResize={handleCRBGridsResize}
        >
          <AutocodeGrid
            title={getGridColumns('Rows')}
            weightType={`${checkWeightType(weightTypes)}`}
            gridCategory={ROWS_DATA_GRID}
            minRows={6}
            gridarray={cloneDeep(allRowsLists)}
            onGridDrop={handleGridDrop}
            height="164px"
            width={CRBGridsWidth}
            selectedData={selectedRowsData}
            clearSelections={clearSelections}
            setCheckBlur={setCheckBlur}
            partsToShow={partsToShow}
            handleRowMouseDown={handleRowMouseDown}
            handleRowMouseUp={handleRowMouseUp}
          />
        </ResizeAware>
      </ACGridContext.Provider>
      {showDeleteAllModal && (
        <ACCRBGridDeleteModal
          gridType={deleteAllGridType}
          show={showDeleteAllModal}
          closeHandler={() => {
            setShowDeleteAllModal(false);
            setDeleteAllGridType('');
          }}
          deleteHandler={() => {
            const { DELETE_ALL } = MenuTypesOptions.CONTEXT_MENU_OPTIONS;
            handleContextMenuOptions({
              option: DELETE_ALL,
              data: [],
              gridCategory: deleteAllGridType,
            });
            setShowDeleteAllModal(false);
            setDeleteAllGridType('');
          }}
        />
      )}
    </Fragment>
  );
};

AutocodeCRBGrid.propTypes = {
  weightTypes: PropTypes.shape({}).isRequired,
  gridTitle: PropTypes.string,
  BasesDataGridData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ColumnsDataGridData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  RowsDataGridData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  moduleAction: PropTypes.shape({
    updateSampleWeighted: PropTypes.func,
    updateCrosstabGridTitleHeader: PropTypes.func,
    deleteAutocodeGridData: PropTypes.func,
  }).isRequired,
  moduleStudy: PropTypes.shape({
    st: PropTypes.string,
  }),
  gridData: PropTypes.shape({
    BasesDataGrid: PropTypes.arrayOf(PropTypes.object),
    ColumnsDataGrid: PropTypes.arrayOf(PropTypes.object),
    RowsDataGrid: PropTypes.arrayOf(PropTypes.object),
  }),
  selectedWeightType: PropTypes.shape({}),
  activeAccountId: PropTypes.number,
  uid: PropTypes.number,
  sampleWeightedKPIs: PropTypes.shape({}),
  partsToShow: PropTypes.shape({}),
};

export default AutocodeCRBGrid;
